<template>
  <div class="content">
    <!-- 保存中は画面全体を白く覆う（清掃員・家族・緊急連絡先の保存と、契約詳細・雇用指示の行の保存。賃金画面と同じ見た目） -->
    <div v-if="saving || conSaving" class="loading-mask">
      保存中…
    </div>

    <PageHeader title="AKBS登録済み一覧">
      <template #title-header-content>
        <!-- 軸の切り替え（清掃員・雇用指示・契約詳細）。一番上の行の真ん中に白い枠で置く。軸ごとに一覧と絞り込みを持つ -->
        <div class="axis-tabs">
          <button
            v-for="a in axes"
            :key="a.key"
            type="button"
            class="axis-tab"
            :class="axis === a.key ? 'active' : ''"
            @click="changeAxis(a.key)"
          >
            <Icon :iconName="a.icon" class="w-4 h-4 mr-1" />{{ a.name }}
          </button>
        </div>

        <div class="flex-1 flex items-center justify-end mr-2">
          <!-- Excel 出力（今の軸・検索条件で全件。出す項目はモーダルで選ぶ）。Excel と分かるように緑＋ダウンロードのアイコン -->
          <PrimaryButton text="Excel出力" class="w-36 mr-3 bg-green-700 hover:bg-green-800" title="今の検索条件で全件を Excel に出します（出す項目を選べます）" @click="excelOpen = true">
            <template #before>
              <Icon iconName="DocumentDownload" iconType="outline" :strokeWidth="2" class="w-5 h-5" />
            </template>
          </PrimaryButton>
          <PrimaryButton text="検索" class="w-32" @click="getList(1)" />
          <div class="ml-4 text-lg font-bold text-white">{{ cur.records }} {{ axisDef.unit }}</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': cur.loading }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="getList()"
          />
        </div>
      </template>

      <template #page-header-content>
        <div class="flex items-center justify-end gap-4">
          <!-- 絞り込み（1欄で名前・コードを検索。「〜のみ」は初期値オン。何かで絞っている欄は黄色） -->
          <div class="flex items-center gap-2">
            <!-- 契約詳細だけ：ON にすると契約の行の下に、その契約の雇用指示（清掃員1人1行）が出る。範囲の絞り込みは雇用指示にも同じく当たる -->
            <label v-if="axis === 'contract'" class="flex items-center gap-1 text-sm text-white cursor-pointer select-none whitespace-nowrap mr-2">
              <input v-model="cur.filter.withEmployment" type="checkbox" class="rounded" @change="getList()" />
              雇用指示も表示
            </label>
            <input
              v-model="cur.filter.name"
              type="search"
              :placeholder="axisDef.placeholder"
              class="filter-input w-80 rounded border border-gray-300 px-2 text-sm"
              :class="cur.filter.name ? 'filled' : ''"
              @keydown.enter="getList(1)"
            />
            <!-- 範囲（清掃員：在職者・退職者・全員。雇用指示・契約詳細：終了していない・終了した・全て）。「全て」以外は絞っているので黄色 -->
            <select v-model="cur.filter.scope" class="filter-input rounded border-gray-300 text-sm" :class="cur.filter.scope !== 'all' ? 'filled' : ''" title="範囲" @change="getList(1)">
              <option v-for="s in axisDef.scopes" :key="s.value" :value="s.value">{{ s.name }}</option>
            </select>
          </div>
        </div>
      </template>
    </PageHeader>

    <main class="absolute top-32 -mt-1 bottom-4 w-screen">
      <!-- ========== 雇用指示・契約詳細を軸にしたとき：左右に分けず、一覧そのものに1行ずつ全部の内容を出す（賃金は出さない） ========== -->
      <div v-if="axis !== 'staff'" class="h-full px-4 flex flex-col">
        <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
          <div v-if="cur.loading" class="flex h-full items-center justify-center font-bold text-gray-400">
            Searching...
          </div>

          <div v-else-if="!cur.list.length" class="flex h-full items-center justify-center font-bold text-gray-400">
            対象データがありません。
          </div>

          <!-- 1つの表に全部の内容を出す（上下で見比べられるように）。横に長いので横スクロール、見出しは固定 -->
          <table v-else class="emp-table">
            <thead>
              <tr>
                <th class="text-left site">物件</th>
                <th>契約コード<br />作業内容</th>
                <th class="text-left">清掃員</th>
                <th>{{ isEmploymentAxis ? '期間' : '契約期間' }}</th>
                <th v-for="w in weekList" :key="w.id" class="day" :class="[w.key === 'sat' ? 'sat' : '', w.key === 'sun' ? 'sun' : '']">{{ w.name }}</th>
                <th>祝日</th>
                <th>ゴミ出し</th>
                <th>全社休日<br />出勤</th>
                <th v-if="!isEmploymentAxis">時間指定</th>
                <!-- ランク・クリーンプラス金額（契約詳細情報マスタの列。契約詳細の軸だけ） -->
                <th v-if="!isEmploymentAxis">ランク</th>
                <th v-if="!isEmploymentAxis">クリーン<br />プラス</th>
                <th class="text-left">備考</th>
              </tr>
            </thead>
            <tbody>
              <!--
                行は tableRows（契約詳細のときは、契約の行の下にその雇用指示の行がぶら下がる）
                物件が変わる行は上に濃い線（同じ物件の行がひとかたまりに見える）。ぶら下がった雇用指示の行は薄い黄色
              -->
              <tr v-for="r in tableRows" :key="r.key" :class="[r.row.isEnded ? 'ended' : '', r.siteTop ? 'site-top' : '', r.nested ? 'nested' : '']">
                <!-- 物件（名前・コード・住所・得意先）。横スクロールしても左に残す -->
                <td v-if="!r.nested" class="text-left site">
                  <div class="site-name">{{ siteNameText(r.row) }}<Badge v-if="r.row.isEnded" color="red" class="ml-2 align-middle">終了</Badge></div>
                  <!-- 契約の行のスマイルの営業所コードは出さない（ユーザー指示）。雇用指示の行は最低賃金判定（東京／千葉。最低賃金を比べる営業所） -->
                  <div class="sub">物件コード：{{ r.row.cdSite }}<span v-if="r.emp" class="ml-3" v-tooltip="salesOfficeTip">{{ salesOfficeCaption }}：{{ r.row.salesOffice || '-' }}</span></div>
                  <!-- 雇用指示営業所は画面に出さない（データは持つ） -->
                  <div v-if="r.emp" class="sub">エリア：{{ r.row.nameSalesOfficeCharge || '-' }}</div>
                  <div v-if="r.row.siteAddress" class="sub">{{ r.row.siteAddress }}</div>
                  <!-- 得意先名（「得意先：」の頭は付けない。無くても分かる。ユーザー指示） -->
                  <div v-if="r.row.client1 || r.row.client2" class="sub">{{ r.row.client1 }} {{ r.row.client2 }}</div>
                  <!-- 行の編集の入口。契約の行（契約詳細権限）：作業内容・時間指定・ランク・クリーンプラス金額・備考。雇用指示の行（雇用指示権限。雇用指示の軸だけ）：作業内容・備考
                       保存・取消も同じ場所（右端の備考セルは PC で切れて見えないため。ユーザー指示） -->
                  <template v-if="canEditRow(r)">
                    <button v-if="!isConEditing(r)" type="button" class="con-btn edit mt-1" :disabled="!!conEdit" :title="r.emp ? 'この行の作業内容・備考を直します' : 'この行の作業内容・時間指定・ランク・クリーンプラス金額・備考を直します'" @click="startConEdit(r)">編集</button>
                    <div v-else class="mt-1">
                      <div class="flex items-center gap-2">
                        <button type="button" class="con-btn save" :disabled="conSaving" @click="saveConEdit(r)">{{ conSaving ? '保存中…' : '保存' }}</button>
                        <button type="button" class="con-btn" :disabled="conSaving" @click="cancelConEdit">取消</button>
                      </div>
                      <div class="text-xs text-red-600 font-bold mt-1 whitespace-normal">保存すると本番のAKBSの{{ r.emp ? '雇用指示' : '契約詳細情報' }}が書き換わります</div>
                    </div>
                  </template>
                </td>
                <!-- ぶら下がった雇用指示の行は、物件名を繰り返さず「雇用指示」のバッジと清掃員だけ -->
                <td v-else class="text-left site">
                  <div class="flex items-center gap-2">
                    <Badge color="yellow">雇用指示</Badge>
                    <span class="font-bold text-indigo-900">{{ r.row.nameStaff || '-' }}</span>
                    <span class="sub">（{{ r.row.staffDailyCleaning }}）</span>
                    <Badge v-if="r.row.isEnded" color="red">終了</Badge>
                  </div>
                  <div class="sub" v-tooltip="salesOfficeTip">{{ salesOfficeCaption }}：{{ r.row.salesOffice || '-' }}</div>
                  <div class="sub">エリア：{{ r.row.nameSalesOfficeCharge || '-' }}</div>
                  <button type="button" class="link-btn" @click="toStaff(r.row.staffDailyCleaning)">清掃員の内容を見る</button>
                </td>
                <td>
                  <div class="font-bold">{{ r.row.cdContract || '-' }}</div>
                  <!-- 契約の行の編集中は作業内容を選べる -->
                  <select v-if="isConEditing(r)" v-model="conEdit.cdContentsWork" class="con-edit-select" :class="isConChanged('cdContentsWork') ? 'changed' : ''">
                    <option value="">-</option>
                    <option v-for="w in workSelections" :key="w.id" :value="w.id">{{ w.name }}</option>
                  </select>
                  <div v-else class="sub">{{ r.row.contentsWorkName || r.row.cdContentsWork || '-' }}</div>
                </td>
                <!-- 清掃員（雇用指示の軸はその行の1人。契約の行はその契約で働いている人数。ぶら下がった雇用指示の行は左に出しているので空） -->
                <td v-if="r.emp && !r.nested" class="text-left">
                  <div class="font-bold text-indigo-900">{{ r.row.nameStaff || '-' }}</div>
                  <div class="sub">（{{ r.row.staffDailyCleaning }}）</div>
                  <button type="button" class="link-btn" @click="toStaff(r.row.staffDailyCleaning)">清掃員の内容を見る</button>
                </td>
                <td v-else-if="!r.emp" class="whitespace-nowrap">
                  <span class="font-bold" :class="(r.row.staffs || []).length ? 'text-indigo-900' : ''">{{ (r.row.staffs || []).length }}名</span>
                </td>
                <td v-else></td>
                <!-- 期間（開始日・～・終了日を3行に） -->
                <td class="whitespace-nowrap">
                  <div>{{ slash(rowStart(r.row, r.emp)) || '-' }}</div>
                  <div class="tilde">～</div>
                  <div :class="r.row.isEnded ? 'text-red-700 font-bold' : ''">{{ slash(rowEnd(r.row, r.emp)) }}</div>
                </td>
                <!-- 曜日ごと（時間・実働・勤務形態。休みは薄い灰色に「-」） -->
                <td v-for="w in weekList" :key="w.id" class="day" :class="isDayOff(r.row, w) ? 'rest' : 'work'">
                  <template v-if="isDayOff(r.row, w)">-</template>
                  <!-- 上から勤務形態・時間・実働 -->
                  <template v-else>
                    <div>
                      <span v-for="(name, ai) in attendanceNames(r.row['attendance' + w.id])" :key="ai" class="att-pill">{{ name }}</span>
                    </div>
                    <div class="time">{{ r.row['start' + w.id] || '-' }}<span class="tilde">～</span>{{ r.row['end' + w.id] || '-' }}</div>
                    <div class="hours">{{ hoursText(r.row['workingHours' + w.id]) }}</div>
                  </template>
                </td>
                <!-- 祝日・ゴミ出し・全社休日出勤（契約詳細は時間指定も）はバッジ（有は藍色、無は灰色。未設定は -） -->
                <td>
                  <Badge v-if="hasValue(rowHoliday(r.row, r.emp))" :color="flagColor(rowHoliday(r.row, r.emp))">{{ holidayName(rowHoliday(r.row, r.emp)) }}</Badge>
                  <template v-else>-</template>
                </td>
                <td v-for="f in rowFlags(r.row, r.emp)" :key="f.key">
                  <!-- 契約の行の編集中は時間指定を選べる -->
                  <select v-if="f.key === 'specificTime' && isConEditing(r)" v-model="conEdit.specificTime" class="con-edit-select narrow" :class="isConChanged('specificTime') ? 'changed' : ''">
                    <option value="">-</option>
                    <option value="1">有</option>
                    <option value="0">無</option>
                  </select>
                  <Badge v-else-if="hasValue(f.value)" :color="flagColor(f.value)">{{ yesNo(f.value) }}</Badge>
                  <template v-else>-</template>
                </td>
                <!-- ランク・クリーンプラス金額（契約の行だけ。ぶら下がった雇用指示の行は空。編集中は選べる・入れられる） -->
                <template v-if="!isEmploymentAxis">
                  <td class="whitespace-nowrap">
                    <select v-if="isConEditing(r) && !r.emp" v-model="conEdit.rank" class="con-edit-select narrow" :class="isConChanged('rank') ? 'changed' : ''">
                      <option value="">-</option>
                      <option v-for="rk in rankList" :key="rk" :value="rk">{{ rk }}</option>
                    </select>
                    <template v-else-if="!r.emp">
                      <Badge v-if="hasValue(r.row.rank)" color="indigo">{{ r.row.rank }}</Badge>
                      <template v-else>-</template>
                    </template>
                  </td>
                  <td class="whitespace-nowrap">
                    <!-- 数値入力はホイールで動かさない（@wheel で blur） -->
                    <input v-if="isConEditing(r) && !r.emp" v-model="conEdit.cleanPlus" type="number" min="0" class="con-edit-input" :class="isConChanged('cleanPlus') ? 'changed' : ''" placeholder="円" @wheel="$event.target.blur()" />
                    <template v-else-if="!r.emp">{{ hasValue(r.row.cleanPlus) ? Number(r.row.cleanPlus).toLocaleString() + '円' : '-' }}</template>
                  </td>
                </template>
                <!-- 雇用指示の行：備考（この列だけ折り返す。編集中は入力欄）。その下にシーケンスと更新日時を小さく添える -->
                <td v-if="r.emp" class="text-left wide note-cell">
                  <textarea v-if="isConEditing(r)" v-model="conEdit.note" class="con-edit-note" :class="isConChanged('note') ? 'changed' : ''" rows="3" placeholder="備考"></textarea>
                  <div v-else-if="r.row.note" class="note">{{ r.row.note }}</div>
                  <!-- セルの下に固定 -->
                  <div class="note-meta">
                    <div class="sub">シーケンス：{{ r.row.sequenceNumber }}<span v-if="r.row.changeFromSequenceNumber">（契約変更前：{{ r.row.changeFromSequenceNumber }}）</span></div>
                    <div class="sub">更新日時：{{ r.row.updateDate }}</div>
                  </div>
                </td>
                <!-- 契約の行：備考と変更前の物件コード（契約詳細情報マスタの列）。その下に更新日時を小さく添える（雇用指示の行と同じ作り）
                     契約詳細権限がある人は「編集」で、この行の作業内容・時間指定・ランク・クリーンプラス金額・備考を直せる（保存で本番の契約詳細情報マスタを書き換える） -->
                <td v-else class="text-left wide note-cell">
                  <div v-if="r.row.oldCdSite" class="sub mb-1">物件コード変更：{{ r.row.oldCdSite }} ⇒ {{ r.row.cdSite }}<template v-if="r.row.cdSiteChangeDate">（{{ r.row.cdSiteChangeDate }}）</template></div>
                  <!-- 編集中は備考の入力欄（保存・キャンセルは物件のセル） -->
                  <textarea v-if="isConEditing(r)" v-model="conEdit.note" class="con-edit-note" :class="isConChanged('note') ? 'changed' : ''" rows="3" placeholder="備考"></textarea>
                  <div v-else-if="r.row.note" class="note">{{ r.row.note }}</div>
                  <div class="note-meta">
                    <div class="sub">更新日時：{{ r.row.updateDate }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ページネーション（左右に分けた軸と同じ部品・見た目） -->
        <div class="my-2 w-fit mx-auto">
          <pagination id="pagenation" v-model="cur.page" :per-page="perPage" :records="cur.records" class="text-center" @paginate="getList()" />
        </div>
      </div>

      <!-- ========== 清掃員を軸にしたとき：左に一覧、右に選んだ清掃員の内容 ========== -->
      <div v-else class="h-full px-4 flex gap-4">

        <!-- 左：一覧（1つ選ぶと右に内容が出る） -->
        <div class="list-box flex flex-col flex-shrink-0">
          <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
            <div v-if="cur.loading" class="flex h-full items-center justify-center font-bold text-gray-400">
              Searching...
            </div>

            <div v-else-if="!cur.list.length" class="flex h-full items-center justify-center font-bold text-gray-400">
              対象データがありません。
            </div>

            <!-- 清掃員 -->
            <table v-else class="w-full border-collapse text-sm">
              <thead class="sticky top-0 bg-gray-100">
                <tr class="border-b border-gray-300 text-left text-xs text-gray-600">
                  <th class="px-3 py-2">清掃員</th>
                  <th class="px-3 py-2">物件</th>
                  <th class="px-3 py-2 text-center">状態</th>
                </tr>
              </thead>
              <tbody class="text-gray-700">
                <tr
                  v-for="row in cur.list"
                  :key="row.staffDailyCleaning"
                  class="border-b border-gray-200 cursor-pointer hover:bg-blue-50"
                  :class="row.staffDailyCleaning === cur.selectedKey ? 'selected-row' : ''"
                  @click="select(row)"
                >
                  <td class="px-3 py-2">
                    <div class="font-bold">{{ row.nameStaff }}</div>
                    <div class="text-xs text-gray-500">{{ row.nameKanaStaff }}<span class="ml-2">（{{ row.staffDailyCleaning }}）</span></div>
                  </td>
                  <!-- 物件名は1物件1行で、長いものは … で切る（カーソルを当てると全文） -->
                  <td class="px-3 py-2 text-xs max-w-0 w-2/5">
                    <div v-for="(site, si) in (row.sites || '').split('、').filter(x => x)" :key="si" class="truncate" :title="site">{{ site }}</div>
                  </td>
                  <td class="px-3 py-2 text-center whitespace-nowrap">
                    <Badge :color="isRetired(row) ? 'gray' : 'lightGreen'">{{ isRetired(row) ? '退職' : '在職' }}</Badge>
                  </td>
                </tr>
              </tbody>
            </table>

          </div>

          <!-- ページネーション（申請一覧と同じ部品・見た目） -->
          <div class="my-2 w-fit mx-auto">
            <pagination id="pagenation" v-model="cur.page" :per-page="perPage" :records="cur.records" class="text-center" @paginate="getList()" />
          </div>
        </div>

        <!-- 右：選んだものの内容（すべて閲覧のみ） -->
        <div class="flex-1 min-w-0 flex flex-col rounded-md border border-gray-200 bg-white mb-2">
          <div v-if="cur.detailLoading" class="flex h-full items-center justify-center font-bold text-gray-400">
            Loading...
          </div>

          <div v-else-if="!cur.detail" class="flex h-full items-center justify-center font-bold text-gray-400">
            左の一覧から{{ axisDef.name }}を選んでください。
          </div>

          <!-- ========== 清掃員を軸にした内容 ========== -->
          <template v-else>
            <div class="detail-head">
              <div class="text-xl font-bold text-indigo-900">{{ cur.detail.staff.nameStaff }}</div>
            </div>

            <!-- 清掃員の中のタブ -->
            <div class="flex gap-1 px-4 pt-2 border-b border-gray-200">
              <button
                v-for="t in staffTabs"
                :key="t.key"
                type="button"
                class="tab"
                :class="staffTab === t.key ? 'active' : ''"
                @click="staffTab = t.key"
              >
                <Icon :iconName="t.icon" iconType="solid" class="w-5 h-5 mr-1 flex-none" />{{ t.name }}<span v-if="t.count !== null" class="tab-count">{{ t.count }}</span>
              </button>
            </div>

            <div class="flex-1 min-h-0 overflow-auto p-4">
              <!-- 清掃員マスタ -->
              <div v-if="staffTab === 'staff'">
                <!-- 編集（この画面で直せるのは生年月日・性別・電話番号・携帯番号だけ。AKBS関連メニュー権限がある人＝この画面を見られる人なら誰でも）
                     保存すると本番のAKBSの清掃員マスタを直接書き換える。変更した項目があれば人事総務経理へのメインタスク（業務依頼）を作る -->
                <div class="flex items-center gap-2 mb-2">
                  <template v-if="!editing">
                    <PrimaryButton v-if="canEditStaff" text="編集" class="w-24" @click="startEdit" />
                    <span v-if="canEditStaff" class="text-xs text-gray-500">生年月日・性別・電話番号・携帯番号を直せます（保存すると人事総務経理へのメインタスクが作られます）</span>
                  </template>
                  <template v-else>
                    <PrimaryButton text="保存" class="w-32 bg-green-600" :disabled="saving" @click="saveEdit" />
                    <PrimaryButton text="キャンセル" class="w-32 bg-gray-400" :disabled="saving" @click="cancelEdit" />
                    <span class="text-xs text-red-600 font-bold">保存すると人事総務経理へのメインタスク（業務依頼）が作られます</span>
                  </template>
                </div>

                <div class="detail-grid">
                  <template v-for="(it, i) in staffItems">
                    <!-- この画面で直せる項目には「編集可能」の印（権限が無い人にも出して、どの項目を直せるか分かるようにする。編集中は黄色で分かるので出さない） -->
                    <div :key="'c' + i" class="detail-caption" :class="editing && it.edit ? 'editing' : ''">
                      {{ it.caption }}<span v-if="it.edit && !editing" class="editable-mark" title="「編集」を押すとこの項目を直せます">編集可能</span>
                    </div>
                    <div :key="'v' + i" class="detail-value" :class="[it.pre ? 'whitespace-pre-wrap' : '', editing && it.edit ? 'editing' : '']">
                      <template v-if="editing && it.edit">
                        <input v-if="it.edit.type === 'date'" v-model="edit[it.edit.key]" type="date" class="edit-input" />
                        <select v-else-if="it.edit.type === 'select'" v-model="edit[it.edit.key]" class="edit-input">
                          <option value="">-</option>
                          <option v-for="o in it.edit.options" :key="o" :value="o">{{ o }}</option>
                        </select>
                        <input v-else v-model="edit[it.edit.key]" type="text" class="edit-input" :placeholder="it.edit.format === 'mobile' ? '090-1234-5678' : it.caption" />
                        <!-- 携帯番号は 3桁-4桁-4桁 でないと保存できない（入力画面と同じ決まり） -->
                        <span v-if="it.edit.format === 'mobile' && mobileError" class="ml-2 text-xs text-red-600 font-bold">{{ mobileError }}</span>
                      </template>
                      <template v-else>{{ it.value }}</template>
                    </div>
                  </template>
                </div>
              </div>

              <!-- 家族・緊急連絡先の編集（行の追加・修正・削除。保存で本番の同じ清掃員の分を全件入れ直す） -->
              <div v-else-if="(staffTab === 'family' || staffTab === 'emergency') && listEdit.kind === staffTab">
                <div class="flex items-center gap-2 mb-2">
                  <PrimaryButton text="保存" class="w-32 bg-green-600" :disabled="saving" @click="saveListEdit" />
                  <PrimaryButton text="キャンセル" class="w-32 bg-gray-400" :disabled="saving" @click="cancelListEdit" />
                  <PrimaryButton text="行を追加" class="w-32" :disabled="saving" @click="addListRow" />
                </div>
                <div v-if="!listEdit.rows.length" class="empty-text mb-2">行がありません。「行を追加」で足してください。</div>
                <table v-else class="data-table">
                  <thead>
                    <tr>
                      <th v-for="col in listCols" :key="col.key">{{ col.caption }}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in listEdit.rows" :key="i">
                      <td v-for="col in listCols" :key="col.key" class="align-top">
                        <input
                          v-model="r[col.key]"
                          type="text"
                          class="edit-input"
                          :class="col.wide ? 'wide' : 'narrow'"
                          :list="col.suggest ? 'relation-list' : null"
                          :placeholder="col.placeholder || ''"
                        />
                        <div v-if="colError(col, r)" class="text-xs text-red-600 font-bold">{{ colError(col, r) }}</div>
                      </td>
                      <td class="align-top">
                        <button type="button" class="text-xs text-red-600 underline" @click="removeListRow(i)">削除</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <!-- 続柄の候補（入力画面と同じ一覧。自由入力もできる） -->
                <datalist id="relation-list">
                  <option v-for="rel in relations" :key="rel" :value="rel" />
                </datalist>
              </div>

              <!-- 家族マスタ -->
              <div v-else-if="staffTab === 'family'">
                <div class="flex items-center gap-2 mb-2">
                  <PrimaryButton v-if="canEditStaff" text="編集" class="w-24" @click="startListEdit('family')" />
                </div>
                <div v-if="!cur.detail.family.length" class="empty-text">家族の登録はありません。</div>
                <table v-else class="data-table">
                  <thead>
                    <tr>
                      <th>続柄</th>
                      <th>氏名</th>
                      <th>住所</th>
                      <th>電話番号</th>
                      <th>更新日時</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(f, i) in cur.detail.family" :key="i">
                      <td>{{ f.relationship }}</td>
                      <td class="font-bold">{{ f.name }}</td>
                      <td>{{ f.address }}</td>
                      <td>{{ f.phoneNumber }}</td>
                      <td class="text-xs text-gray-500">{{ f.updateDate }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 緊急連絡先マスタ -->
              <div v-else-if="staffTab === 'emergency'">
                <div class="flex items-center gap-2 mb-2">
                  <PrimaryButton v-if="canEditStaff" text="編集" class="w-24" @click="startListEdit('emergency')" />
                </div>
                <div v-if="!cur.detail.emergency.length" class="empty-text">緊急連絡先の登録はありません。</div>
                <table v-else class="data-table">
                  <thead>
                    <tr>
                      <th>続柄</th>
                      <th>氏名</th>
                      <th>フリガナ</th>
                      <th>〒</th>
                      <th>住所</th>
                      <th>電話番号</th>
                      <th>携帯番号</th>
                      <th>更新日時</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(e, i) in cur.detail.emergency" :key="i">
                      <td>{{ e.relationship }}</td>
                      <td class="font-bold">{{ e.name }}</td>
                      <td>{{ e.kana }}</td>
                      <td>{{ e.zip }}</td>
                      <td>{{ e.address }}</td>
                      <td>{{ e.phoneNumber }}</td>
                      <td>{{ e.mobile }}</td>
                      <td class="text-xs text-gray-500">{{ e.updateDate }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- 雇用指示・賃金（雇用指示1行ごとに、その行の賃金をぶら下げる） -->
              <div v-else-if="staffTab === 'employment'">
                <div class="flex items-center gap-4 mb-3">
                  <label class="flex items-center gap-1 text-sm cursor-pointer select-none">
                    <input v-model="showEnded" type="checkbox" class="rounded" />
                    終了した行・賃金も表示
                  </label>
                  <span v-if="!showEnded && endedEmploymentCount" class="text-xs text-gray-500">（終了した行が {{ endedEmploymentCount }} 行あります）</span>
                </div>
                <div v-if="!staffEmploymentRows.length" class="empty-text">雇用指示の登録はありません。</div>
                <AkbsRegisteredCard v-for="e in staffEmploymentRows" :key="e.sequenceNumber" :row="e" kind="employment" :showEnded="showEnded" />
              </div>
              <!-- 書類：履歴書（雇い入れメインタスクの添付。最新の新規入社 1 つ）、この画面で添付した書類（S3）、顔写真（出力画面と同じもの）
                   タブを押したときに取る（書類タブ以外を重くしないため）。見られる人なら誰でも添付・削除できる。履歴書は消せない -->
              <div v-else-if="staffTab === 'docs'">
                <div v-if="docs.loading" class="empty-text">Loading...</div>
                <div v-else-if="docs.error" class="text-sm text-red-600 font-bold">{{ docs.error }}</div>
                <template v-else-if="docs.loaded">
                  <!-- 顔写真（名札用。出力画面の顔写真と同じ表・同じ API） -->
                  <div class="doc-block">
                    <div class="doc-block-title">顔写真（名札用）</div>
                    <div class="flex items-center gap-3">
                      <!-- あれば小さく出す（押すと元の大きさで別タブ） -->
                      <a v-if="docs.photoUrl" :href="docs.photoUrl" target="_blank" rel="noopener" title="押すと元の大きさで開きます">
                        <img :src="docs.photoUrl" alt="顔写真" class="photo-thumb" />
                      </a>
                      <!-- 写真があるときは写真が見えるので「あり」は出さない -->
                      <span v-if="docs.photoSaving || !docs.hasPhoto" class="font-bold text-gray-500">{{ docs.photoSaving ? '保存中…' : 'なし' }}</span>
                      <label class="file-btn" :class="docs.photoSaving ? 'is-off' : ''" :title="docs.hasPhoto ? '顔写真を別のものに変更します' : '顔写真を登録します（JPEG・PNG。名札用に小さくして保存します）'">
                        <input type="file" accept=".jpg,.jpeg,.png" class="hidden" :disabled="docs.photoSaving" @change="pickPhoto($event)" />{{ docs.hasPhoto ? '変更' : '登録' }}
                      </label>
                      <button v-if="docs.hasPhoto" type="button" class="file-btn danger" :disabled="docs.photoSaving" @click="deletePhoto">削除</button>
                      <span class="text-xs text-gray-500">出力画面の顔写真と同じものです（野村・三菱の名札に入ります）</span>
                    </div>
                  </div>

                  <!-- 書類。追加は表の下のドロップ枠（落とすか、押して選ぶ。複数可。履歴書はボタンからだけ） -->
                  <div class="doc-block">
                    <div class="flex items-center gap-3 mb-2">
                      <div class="doc-block-title mb-0">書類</div>
                      <span class="text-xs text-gray-500">PDF・JPEG・PNG・Word・Excel、10 MB まで。履歴書は消せません</span>
                    </div>
                    <table class="data-table doc-table">
                      <thead>
                        <tr>
                          <th>種類</th>
                          <th>ファイル名</th>
                          <th>登録日</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- 雇い入れタスクの履歴書（最新の新規入社 1 つ） -->
                        <tr v-if="docs.resume">
                          <td class="font-bold">履歴書</td>
                          <td><a :href="resumeHref" target="_blank" rel="noopener" class="doc-link">{{ docs.resume.fileName }}</a></td>
                          <td class="text-xs text-gray-500">{{ docs.resume.createDate }}</td>
                          <td class="text-xs text-gray-400">削除不可</td>
                        </tr>
                        <!-- 取れなかった（表にも無い）：知らせて、同じセルの「履歴書を添付」で入れてもらう（入れたら消せない） -->
                        <tr v-else-if="docs.canAddResume">
                          <td class="font-bold">履歴書</td>
                          <td colspan="3">
                            <div class="flex items-center gap-3">
                              <label class="file-btn resume" :class="docs.saving ? 'is-off' : ''" title="雇い入れタスクに履歴書が無いので、ここから履歴書を入れられます（入れたら消せません）">
                                <input type="file" :accept="docAccept" class="hidden" :disabled="docs.saving" @change="pickDoc('resume', $event)" />履歴書を添付
                              </label>
                              <span class="text-red-600 font-bold">履歴書を取得できませんでした（雇い入れタスクに履歴書が無いか、清掃員コードが紐づいていません）</span>
                            </div>
                          </td>
                        </tr>
                        <!-- この画面で添付した書類（S3） -->
                        <tr v-for="d in docs.rows" :key="d.id">
                          <!-- 種類は自由入力（欄を離れたら即保存）。履歴書は固定 -->
                          <td v-if="d.kind === 'resume'" class="font-bold">履歴書</td>
                          <td v-else>
                            <div class="flex items-center gap-2">
                              <input
                                v-model="d.name"
                                type="text"
                                class="doc-name-input"
                                :class="[d.name ? '' : 'empty', docs.savedId === d.id ? 'saved' : '']"
                                placeholder="種類を入力"
                                :disabled="docs.saving"
                                @change="saveDocName(d)"
                                @keydown.enter="$event.target.blur()"
                              />
                              <!-- 保存した合図（少しの間だけ） -->
                              <span v-if="docs.savedId === d.id" class="doc-saved">
                                <Icon iconName="CheckCircle" iconType="solid" class="w-4 h-4 mr-0.5" />保存しました
                              </span>
                            </div>
                          </td>
                          <td><a :href="d.url" target="_blank" rel="noopener" class="doc-link">{{ d.fileName }}</a><span class="ml-2 text-xs text-gray-400">{{ sizeText(d.fileSize) }}</span></td>
                          <td class="text-xs text-gray-500">{{ d.insertDate }}</td>
                          <td>
                            <span v-if="d.kind === 'resume'" class="text-xs text-gray-400">削除不可</span>
                            <button v-else type="button" class="text-xs text-red-600 underline" :disabled="docs.saving" @click="deleteDoc(d)">削除</button>
                          </td>
                        </tr>
                        <tr v-if="!docs.resume && !docs.canAddResume && !docs.rows.length">
                          <td colspan="4" class="empty-text">書類はありません。</td>
                        </tr>
                      </tbody>
                    </table>
                    <!-- 書類の追加：目立つドロップ枠。label なので押してもファイルを選べる（input は隠す）。
                         枠の中の要素をまたぐたびに dragenter／dragleave が飛ぶので、中は pointer-events: none にして枠だけで受ける -->
                    <label
                      class="doc-drop"
                      :class="[docs.dragging ? 'is-dragging' : '', docs.saving ? 'is-off' : '']"
                      title="この清掃員の書類（返送された書類など）を添付します"
                      @dragenter.prevent="onDocDragEnter"
                      @dragover.prevent
                      @dragleave="onDocDragLeave"
                      @drop.prevent="onDocDrop"
                    >
                      <input type="file" :accept="docAccept" class="hidden" multiple :disabled="docs.saving" @change="pickDoc('other', $event)" />
                      <Icon iconName="DocumentAdd" iconType="outline" :strokeWidth="2" class="w-10 h-10 doc-drop-icon" />
                      <div>
                        <div class="doc-drop-main">{{ docs.saving ? '保存中…' : docs.dragging ? 'ここに落とすと添付します' : 'ここにファイルをドラッグ＆ドロップ' }}</div>
                        <div v-if="!docs.saving" class="doc-drop-sub">クリックするとフォルダから選択可能（複数可）</div>
                      </div>
                    </label>
                  </div>
                </template>
              </div>
              <!-- 契約詳細のタブは無い（契約詳細は契約詳細の軸で見る。2026-09-22 ユーザー指示） -->
            </div>
          </template>
        </div>
      </div>
    </main>

    <!-- Excel 出力の項目選択（今の軸・検索条件で全件。モーダルの中でダウンロードまで行う） -->
    <AkbsExcelModal
      v-model="excelOpen"
      :open="excelOpen"
      :axis="axis"
      :filter="cur.filter"
      :conditionText="excelConditionText"
    />
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import Badge from '@components/Badge.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import Pagination from 'vue-pagination-2'
import moment from 'moment'
import AkbsRegisteredCard from '../components/AkbsRegisteredCard.vue'
import AkbsExcelModal from '../components/AkbsExcelModal.vue'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as helper from '@libs/helper'
import * as utils from '@libs/utils'
import * as utilsAKBS from '@libs/utilsAKBS'
import * as requestPostManager from '@managers/requestPostManager'
import { FAMILIY_RELATIONS } from '@libs/constants'
import { WEEK_LIST, SALES_OFFICE_CAPTION, SALES_OFFICE_TIP, WORK_SELECTIONS } from '@libs/constantsAKBS'
import PageModel from '../../main/pages/PageModel.vue'

// 絞り込みを控えるセッションストレージのキー（申請一覧と同じ仕組み）
const SESSION_STORAGE_NAME = 'akbsRegisteredListStorage'

// 軸（ヘッダーのタブ）。API 名・一覧の単位・絞り込みの文言・範囲の選択肢（値はバックの scope）
const AXES = [
  {
    key: 'staff', name: '清掃員', icon: 'User', unit: '人', placeholder: '清掃員名・フリガナ・コード 検索',
    scopes: [{ value: 'active', name: '在職者' }, { value: 'ended', name: '退職者' }, { value: 'all', name: '全員' }],
    listApi: 'getStaffList', detailApi: 'getStaffDetail'
  },
  {
    // 雇用指示は一覧そのものに全部の内容を出すので、選んで内容を取る API（detailApi）は無い
    key: 'employment', name: '雇用指示', icon: 'ClipboardList', unit: '行', placeholder: '物件名・物件カナ・清掃員名・コード 検索',
    scopes: [{ value: 'active', name: '終了していない行' }, { value: 'ended', name: '終了した行' }, { value: 'all', name: '全て' }],
    listApi: 'getEmploymentList'
  },
  {
    // 契約詳細も一覧そのものに全部の内容を出すので detailApi は無い
    key: 'contract', name: '契約詳細', icon: 'OfficeBuilding', unit: '件', placeholder: '物件名・物件カナ・物件コード・契約コード 検索',
    scopes: [{ value: 'active', name: '終了していない契約' }, { value: 'ended', name: '終了した契約' }, { value: 'all', name: '全て' }],
    listApi: 'getContractList'
  }
]

// 性別の選択肢（入力画面 AkbsInputForm と同じ値）
const SEX_OPTIONS = ['男性', '女性']

// 家族・緊急連絡先の編集の列（入力画面 AkbsInputForm の family / emergency と同じ項目。format は形式チェック）
const LIST_COLS = {
  family: [
    { key: 'relationship', caption: '続柄', suggest: true },
    { key: 'name', caption: '氏名' },
    { key: 'address', caption: '住所', wide: true },
    { key: 'phoneNumber', caption: '電話番号' }
  ],
  emergency: [
    { key: 'relationship', caption: '続柄', suggest: true },
    { key: 'name', caption: '氏名' },
    { key: 'kana', caption: 'フリガナ（保存時に半角）' },
    { key: 'zip', caption: '〒', format: 'zip', placeholder: '123-4567' },
    { key: 'address', caption: '住所', wide: true },
    { key: 'phoneNumber', caption: '電話番号' },
    { key: 'mobile', caption: '携帯番号', format: 'mobile', placeholder: '090-1234-5678' }
  ]
}

/**
 * 形式チェック（入力があって形式が違えば文言。入力画面 AkbsInputForm の errText と同じ判定）
 * @param {String} format 'mobile'（3桁-4桁-4桁）か 'zip'（3桁-4桁）
 * @param {String} value 値
 */
function formatError (format, value) {
  const v = String(value || '').trim()
  if (!v) {
    return ''
  }
  if (format === 'mobile' && !/^[0-9]{3}-[0-9]{4}-[0-9]{4}$/.test(v)) {
    return '【3桁-4桁-4桁】の形式で入力してください'
  }
  if (format === 'zip' && !/^[0-9]{3}-[0-9]{4}$/.test(v)) {
    return '【3桁-4桁】の形式で入力してください'
  }
  return ''
}

// 清掃員の中のタブ（契約詳細は契約詳細の軸で見るので、ここには無い）
// タブ（icon は Icon 部品の名前。タブが増えたので見分けやすいようにアイコン付き）
const STAFF_TABS = [
  { key: 'staff', name: '清掃員', icon: 'User' },
  { key: 'family', name: '家族', icon: 'UserGroup' },
  { key: 'emergency', name: '緊急連絡先', icon: 'Phone' },
  { key: 'employment', name: '雇用・賃金', icon: 'CurrencyYen' },
  { key: 'docs', name: '書類', icon: 'DocumentText' }
]

// 清掃員タブの編集を保存したときに作るメインタスクの宛先（部署は名前で引く。担当者は「未定」）
// マスタに見つからないときの id は面接→雇い入れ（requestPostManager.setEmployFromInterview）と同じ
const CHANGE_TASK_DEPARTMENT = '人事総務経理'
const CHANGE_TASK_DEPARTMENT_ID = 'd1449'
const CHANGE_TASK_STAFF = '未定'
const CHANGE_TASK_STAFF_ID = '6419'

// 契約詳細のランクの選択肢（契約詳細入力画面と同じ）
const RANK_SELECTIONS = ['A', 'B', 'C', 'D']

// 「書類」タブで添付できるファイルの種類（バックの STAFF_DOC_TYPES と同じ）と大きさの上限
const DOC_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx']
const DOC_MAX_BYTES = 10 * 1024 * 1024

/**
 * 「書類」タブの状態の初期値（清掃員を選び直したら戻す）
 */
function createDocsState () {
  return {
    loading: false,
    loaded: false,
    error: '',
    // 雇い入れタスクの履歴書（{ fileName, url, taskId, requestNo, createDate } か null）
    resume: null,
    // この画面で添付した書類
    rows: [],
    hasPhoto: false,
    // 顔写真の期限付き URL（小さく出す用。無ければ ''）
    photoUrl: '',
    // タスクからも表からも履歴書が取れない（この画面から「履歴書」として入れられる）
    canAddResume: false,
    saving: false,
    photoSaving: false,
    // 種類を保存した直後の行の id（「保存しました」を少しの間出す）
    savedId: '',
    // 書類の枠の上にファイルを持ってきている（枠を光らせる）
    dragging: false,
    // dragenter／dragleave は枠の中の要素をまたぐたびに飛ぶので、数えて 0 に戻ったら枠を出たとみなす
    dragDepth: 0
  }
}

/**
 * ファイル名の拡張子（小文字。無ければ ''）
 */
function extOf (fileName) {
  const m = String(fileName || '').match(/\.([A-Za-z0-9]+)$/)
  return m ? m[1].toLowerCase() : ''
}

/**
 * ファイル名から拡張子を除く（種類の初期値に使う）
 */
function stripExt (fileName) {
  return String(fileName || '').replace(/\.[A-Za-z0-9]+$/, '')
}

/**
 * ドラッグしているものにファイルが含まれるか（文字や画像のドラッグでは枠を光らせない）
 */
function hasFiles (event) {
  const types = event.dataTransfer ? Array.from(event.dataTransfer.types || []) : []
  return types.includes('Files')
}

/**
 * File → base64 の data URL（バックへ送って S3 に上げる）
 */
function readFileAsDataUrl (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * 軸ごとの状態（一覧・絞り込み・選んだもの・内容）の初期値
 */
function createAxisState () {
  return {
    loading: false,
    list: [],
    page: 1,
    records: 0,
    // 範囲の初期値は「在職者」「終了していない行」（active）。withEmployment は契約詳細だけが使う（雇用指示も表示）
    filter: { name: '', scope: 'active', withEmployment: false },
    detailLoading: false,
    selectedKey: '',
    detail: null
  }
}

export default {
  extends: PageModel,

  components: {
    Icon,
    Badge,
    PageHeader,
    PrimaryButton,
    Pagination,
    AkbsRegisteredCard,
    AkbsExcelModal
  },

  data() {
    return {
      // 最低賃金判定（東京／千葉）の呼び方と説明
      salesOfficeCaption: SALES_OFFICE_CAPTION,
      salesOfficeTip: SALES_OFFICE_TIP,

      // 今の軸
      axis: 'staff',
      axes: AXES,
      // 軸ごとの状態（切り替えても検索結果・選んだものを持ち続ける）
      state: {
        staff: createAxisState(),
        employment: createAxisState(),
        contract: createAxisState()
      },
      perPage: 50,
      // 曜日（雇用指示の一覧表の列）
      weekList: WEEK_LIST,

      // 清掃員の中のタブ
      staffTab: 'staff',
      // 終了した雇用指示・賃金も出すか
      showEnded: false,

      // 雇用指示権限（雇用指示の一覧の行を直せるか。created で入れる）
      akbsAuth: false,
      // 清掃員・家族・緊急連絡先を直せるか＝AKBS関連メニュー権限（この画面を見られる人なら誰でも。ユーザー決定）
      canEditStaff: false,
      // 清掃員の編集中か・保存中か
      editing: false,
      saving: false,
      // 編集中の値（直せる4項目。キーはここで揃えておく）
      edit: { birthday: '', sex: '', numberPhoneStaff: '', numberMobilePhoneStaff: '' },

      // 家族・緊急連絡先の編集（kind が 'family' か 'emergency' のとき編集中。rows は編集用の複製）
      listEdit: { kind: '', rows: [] },
      // 続柄の候補
      relations: FAMILIY_RELATIONS.map(r => r.text),

      // 「書類」タブ（タブを押したときに取る）
      docs: createDocsState(),
      docAccept: DOC_EXTS.map(e => '.' + e).join(','),

      // 契約詳細の一覧の行の編集（契約詳細権限。1 行ずつ。conEdit は編集中の行のキーと値）
      contractDetailAuth: false,
      conEdit: null,
      conSaving: false,
      workSelections: WORK_SELECTIONS,
      rankList: RANK_SELECTIONS,

      // Excel 出力のモーダルを開いているか
      excelOpen: false
    }
  },

  watch: {
    /**
     * 清掃員の中のタブを切り替えたら、家族・緊急連絡先の編集はやめる。書類タブは開くたびに取り直す（別の PC での添付も見えるように）
     */
    staffTab(v) {
      this.listEdit = { kind: '', rows: [] }
      if (v === 'docs') {
        this.loadDocs()
      }
    }
  },

  computed: {
    /**
     * 今の軸の定義
     */
    axisDef() {
      return AXES.find(a => a.key === this.axis) || AXES[0]
    },

    /**
     * 今の軸の状態
     */
    cur() {
      return this.state[this.axis]
    },

    /**
     * 雇用指示の軸か（一覧表は雇用指示と契約詳細で同じ作り。列の出し分けに使う）
     */
    isEmploymentAxis() {
      return this.axis === 'employment'
    },

    /**
     * Excel 出力のモーダルの副題に出す、今の検索条件の説明（範囲の名前と検索欄の文字）
     */
    excelConditionText() {
      const f = this.cur.filter
      const scope = this.axisDef.scopes.find(s => s.value === f.scope)
      const parts = [scope ? scope.name : '']
      if (f.name) {
        parts.push(`「${f.name}」`)
      }
      return parts.filter(x => x).join('・')
    },

    /**
     * 一覧表の行（雇用指示・契約詳細の軸で使う）
     * 契約詳細で「雇用指示も表示」が ON のときは、契約の行の下にその雇用指示の行（nested）をぶら下げる
     * @returns [{ key, row, emp: 雇用指示の行か, nested: 契約の下にぶら下がった行か, siteTop: 前の行と物件が変わるか }]
     */
    tableRows() {
      if (this.axis === 'staff') {
        return []
      }
      const rows = []
      let prevSite = null
      for (const row of this.cur.list) {
        if (this.isEmploymentAxis) {
          rows.push({ key: `e-${row.sequenceNumber}`, row, emp: true, nested: false, siteTop: prevSite !== null && row.cdSite !== prevSite })
        } else {
          rows.push({ key: `c-${this.contractKey(row)}`, row, emp: false, nested: false, siteTop: prevSite !== null && row.cdSite !== prevSite })
          for (const e of row.employment || []) {
            rows.push({ key: `e-${e.sequenceNumber}`, row: e, emp: true, nested: true, siteTop: false })
          }
        }
        prevSite = row.cdSite
      }
      return rows
    },

    /**
     * 清掃員の中のタブ（件数付き。清掃員は件数なし）
     */
    staffTabs() {
      const d = this.state.staff.detail
      return STAFF_TABS.map(t => {
        let count = null
        if (d && t.key === 'docs') {
          // 書類はタブを開いたときに取るので、取るまでは件数なし
          count = this.docs.loaded ? (this.docs.resume ? 1 : 0) + this.docs.rows.length : null
        } else if (d && t.key !== 'staff') {
          count = t.key === 'employment' ? this.staffEmploymentRows.length : (d[t.key] || []).length
        }
        return { ...t, count }
      })
    },

    /**
     * 雇い入れタスクの履歴書を開く URL（添付の URL に token を付ける。添付部品と同じ組み方）
     */
    resumeHref() {
      const r = this.docs.resume
      const user = this.$store.getters.user
      return r ? helper.buildAttachmentUrl(r.url, user ? user.token : '') : ''
    },

    /**
     * 清掃員を軸にしたときの雇用指示の行（終了した行は showEnded のときだけ）
     */
    staffEmploymentRows() {
      return this.filterEnded(this.state.staff.detail ? this.state.staff.detail.employment : [])
    },

    /**
     * 隠れている終了した雇用指示の行の数（今の軸の内容から）
     */
    endedEmploymentCount() {
      const d = this.cur.detail
      const rows = d && Array.isArray(d.employment) ? d.employment : []
      return rows.filter(e => e.isEnded).length
    },

    /**
     * 携帯番号の形式エラー（入力があって 3桁-4桁-4桁 でなければ文言。入力画面 AkbsInputForm と同じ判定）
     */
    mobileError() {
      return formatError('mobile', this.edit.numberMobilePhoneStaff)
    },

    /**
     * 編集中の家族・緊急連絡先の列
     */
    listCols() {
      return LIST_COLS[this.listEdit.kind] || []
    },

    /**
     * 清掃員マスタの表示項目（見出しと値の組）
     */
    staffItems() {
      const d = this.state.staff.detail
      const s = d ? d.staff : null
      if (!s) {
        return []
      }
      const v = x => (x === null || x === undefined || x === '') ? '-' : String(x)
      return [
        { caption: '清掃員コード', value: v(s.staffDailyCleaning) },
        { caption: '氏名', value: v(s.nameStaff) },
        { caption: 'フリガナ', value: v(s.nameKanaStaff) },
        { caption: '生年月日', value: s.birthday ? `${this.slash(s.birthday)}（${this.age(s.birthday)}歳）` : '-', edit: { key: 'birthday', type: 'date' } },
        { caption: '性別', value: v(s.sex), edit: { key: 'sex', type: 'select', options: SEX_OPTIONS } },
        { caption: '郵便番号', value: v(s.zip) },
        { caption: '住所', value: v([s.pref, s.city, s.address].filter(x => x).join('')) },
        { caption: 'マンション名など', value: v(s.address2) },
        { caption: '電話番号', value: v(s.numberPhoneStaff), edit: { key: 'numberPhoneStaff', type: 'text' } },
        { caption: '携帯番号', value: v(s.numberMobilePhoneStaff), edit: { key: 'numberMobilePhoneStaff', type: 'text', format: 'mobile' } },
        { caption: 'シルバーパス支給月', value: s.silverMonth ? `${s.silverMonth}月` : '-' },
        { caption: 'シルバーパス金額', value: this.yen(s.silverPrice) },
        { caption: 'リーダー手当（毎月支給）', value: this.yen(s.leaderAllowance) },
        { caption: '入社日', value: v(this.slash(s.dateHire)) },
        { caption: '退職日', value: v(this.slash(s.dateResignation)) },
        { caption: '退職理由', value: v(s.retireReason), pre: true },
        { caption: '再入社NG', value: s.isBanned ? '再入社NG' : '-' },
        { caption: '入社取消', value: s.isCancelled ? '入社取消' : '-' },
        { caption: '備考', value: v(s.noteStaff), pre: true },
        { caption: '変更履歴', value: v(s.changes), pre: true },
        { caption: '更新日時', value: v(s.updateDate) }
      ]
    }
  },

  async created() {
    const auth = this.$store.getters.auth
    this.akbsAuth = !!(auth && auth.akbsAuth)
    this.canEditStaff = !!(auth && auth.akbsMenuAuth)
    // 契約詳細の一覧の編集は契約詳細権限（雇用指示権限とは別）
    this.contractDetailAuth = !!(auth && auth.contractDetailAuth)
    // 前回の軸・絞り込みが残っていれば同じ状態で開く
    this.getSessionStorage()

    // URL に清掃員コードがあれば（出力画面の名前のリンク）、清掃員の軸でその人を検索して選んだ状態で開く
    const staffCode = this.$route.query.staffCode
    if (staffCode) {
      this.axis = 'staff'
      // tab があれば（サブタスクのボタンから。返送保管 → docs、交通費・緊急連絡先登録 → emergency）、その人のそのタブを開いた状態にする（書類は選んだあとに取る）
      const tab = this.$route.query.tab
      if (tab && STAFF_TABS.some(t => t.key === tab)) {
        this.staffTab = tab
      }
      this.state.staff.filter = { ...this.state.staff.filter, name: String(staffCode), scope: 'all' }
      await this.getList(1)
      const row = this.state.staff.list.find(r => String(r.staffDailyCleaning) === String(staffCode))
      if (row) {
        this.select(row)
      }
      return
    }
    this.getList()
  },

  methods: {
    /**
     * 軸を切り替えて、その軸の今の検索欄の内容で 1 ページ目から検索し直す
     * （前の結果を残すと検索欄と一覧がずれて不具合に見えるので、同じタブをもう一度押したときも取り直す）
     * @param {String} key 軸
     */
    changeAxis(key) {
      this.axis = key
      this.getList(1)
    },

    // ------------------------------------------------------------
    // 契約詳細の一覧の行の編集（作業内容・時間指定・ランク・クリーンプラス金額・備考）
    // ------------------------------------------------------------

    /**
     * この表の行を直せるか。契約の行は契約詳細権限、雇用指示の行は雇用指示権限で雇用指示の軸のときだけ（契約の下にぶら下がった行は不可）
     * @param {Object} r tableRows の 1 つ
     */
    canEditRow(r) {
      if (r.emp) {
        return this.isEmploymentAxis && !r.nested && this.akbsAuth
      }
      return this.contractDetailAuth
    },

    /**
     * この表の行が編集中か（tableRows のキーで見る）
     * @param {Object} r tableRows の 1 つ
     */
    isConEditing(r) {
      return !!this.conEdit && this.conEdit.key === r.key
    },

    /**
     * 行の編集を始める（今の値を編集用に写す。select は文字で持つ）
     * 雇用指示の行は作業内容・備考だけ。契約の行は時間指定・ランク・クリーンプラス金額も
     * @param {Object} r tableRows の 1 つ
     */
    startConEdit(r) {
      const row = r.row
      const v = x => (x === null || x === undefined) ? '' : String(x)
      const values = {
        cdContentsWork: v(row.cdContentsWork),
        specificTime: v(row.specificTime),
        rank: v(row.rank),
        cleanPlus: v(row.cleanPlus),
        note: v(row.note)
      }
      // orig は変えた欄を黄色にする比較用
      this.conEdit = { key: r.key, emp: !!r.emp, ...values, orig: { ...values } }
    },

    /**
     * 編集中の欄の値が元から変わっているか（変えた欄だけ黄色にする）
     * @param {String} key 欄のキー
     */
    isConChanged(key) {
      const e = this.conEdit
      return !!e && String(e[key] || '').trim() !== String(e.orig[key] || '').trim()
    },

    cancelConEdit() {
      this.conEdit = null
    },

    /**
     * 行の編集を保存する（本番の契約詳細情報マスタか雇用指示マスタを書き換える）。保存したら行の値を差し替える
     * @param {Object} r tableRows の 1 つ
     * API: akbsRegisteredList/updateContract（契約の行）、akbsRegisteredList/updateEmployment（雇用指示の行）
     */
    async saveConEdit(r) {
      const e = this.conEdit
      const row = r.row
      if (!e) {
        return
      }
      const user = this.$store.getters.user.id

      // 雇用指示の行：作業内容・備考だけ
      if (r.emp) {
        const answer = await dialogs.showConfirmDialog(
          '雇用指示の保存',
          `${row.nameStaff || ''}（${row.staffDailyCleaning}）の作業内容・備考を本番のAKBSに保存します。よろしいですか？`
        )
        if (answer !== 'YES') {
          return
        }
        this.conSaving = true
        try {
          const res = await backend.postData('akbsRegisteredList/updateEmployment', {
            sequenceNumber: row.sequenceNumber,
            cdContentsWork: e.cdContentsWork,
            note: e.note,
            updateUser: user
          })
          const saved = res.data ? res.data.data : null
          row.cdContentsWork = e.cdContentsWork || null
          row.contentsWorkName = saved ? saved.contentsWorkName : ''
          row.note = String(e.note || '').trim() || null
          if (saved && saved.updateDate) {
            row.updateDate = saved.updateDate
          }
          this.conEdit = null
        } catch (error) {
          console.log(error)
          await dialogs.showErrorDialog('雇用指示の保存', '保存に失敗しました。もう一度お試しください。')
        } finally {
          this.conSaving = false
        }
        return
      }

      // 契約の行
      const cleanPlus = String(e.cleanPlus || '').trim()
      if (cleanPlus && !/^\d+$/.test(cleanPlus)) {
        await dialogs.showErrorDialog('クリーンプラス金額', 'クリーンプラス金額は 0 以上の整数で入れてください。')
        return
      }
      const answer = await dialogs.showConfirmDialog(
        '契約詳細の保存',
        `${this.siteNameText(row)}（契約コード ${row.cdContract}・開始日 ${this.slash(row.startDateContract)}）の作業内容・時間指定・ランク・クリーンプラス金額・備考を本番のAKBSに保存します。よろしいですか？`
      )
      if (answer !== 'YES') {
        return
      }
      this.conSaving = true
      try {
        const res = await backend.postData('akbsRegisteredList/updateContract', {
          cdSite: row.cdSite,
          cdContract: row.cdContract,
          startDateContract: row.startDateContract,
          cdContentsWork: e.cdContentsWork,
          specificTime: e.specificTime,
          rank: e.rank,
          cleanPlus,
          note: e.note,
          updateUser: user
        })
        const saved = res.data ? res.data.data : null
        // 行の値を差し替える（取り直さない。select の文字は元の型に戻す）
        row.cdContentsWork = e.cdContentsWork || null
        row.contentsWorkName = saved ? saved.contentsWorkName : ''
        row.specificTime = e.specificTime === '' ? null : Number(e.specificTime)
        row.rank = e.rank || null
        row.cleanPlus = cleanPlus === '' ? null : Number(cleanPlus)
        row.note = String(e.note || '').trim() || null
        if (saved && saved.updateDate) {
          row.updateDate = saved.updateDate
        }
        this.conEdit = null
      } catch (error) {
        console.log(error)
        await dialogs.showErrorDialog('契約詳細の保存', '保存に失敗しました。もう一度お試しください。')
      } finally {
        this.conSaving = false
      }
    },

    /**
     * 今の軸の一覧を検索する
     * @param {Number} num 表示するページ（検索ボタンは1ページ目、ページの切り替えは指定なし）
     */
    async getList(num) {
      const st = this.cur
      const def = this.axisDef
      // 一覧を取り直すときは契約の行の編集はやめる
      this.conEdit = null
      st.loading = true
      this.setSessionStorage()
      if (num) {
        st.page = num
      }

      try {
        const params = {
          name: st.filter.name,
          scope: st.filter.scope,
          page: st.page,
          perPage: this.perPage
        }
        // 契約詳細で「雇用指示も表示」が ON のときだけ、雇用指示も付けて返してもらう
        if (this.axis === 'contract' && st.filter.withEmployment) {
          params.withEmployment = true
        }
        const res = await backend.searchData(`akbsRegisteredList/${def.listApi}`, params)
        st.list = res.data && Array.isArray(res.data.data) ? res.data.data : []
        st.records = st.list.length ? Number(st.list[0].totalSize) : 0
      } catch (error) {
        console.log(error)
        st.list = []
        st.records = 0
      } finally {
        st.loading = false
      }
    },

    /**
     * 一覧の行を選ぶ（右に内容を出す）
     * @param {Object} row 一覧の行
     */
    async select(row) {
      const st = this.cur
      const def = this.axisDef

      // 選ぶのは清掃員だけ（雇用指示・契約詳細は一覧に全部出すので選ぶことは無い）
      if (this.axis !== 'staff' || !def.detailApi) {
        return
      }
      const key = row.staffDailyCleaning
      const params = { staffCode: row.staffDailyCleaning }
      if (!key) {
        return
      }

      // 別のものを選んだら編集はやめる。書類も選んだ人の分に取り直す
      this.editing = false
      this.listEdit = { kind: '', rows: [] }
      this.docs = createDocsState()
      st.selectedKey = key
      st.detailLoading = true
      try {
        const res = await backend.searchData(`akbsRegisteredList/${def.detailApi}`, params)
        st.detail = res.data ? res.data.data : null
      } catch (error) {
        console.log(error)
        st.detail = null
        await dialogs.showErrorDialog('登録内容の取得', '登録内容の取得に失敗しました。もう一度お試しください。')
      } finally {
        st.detailLoading = false
      }
      if (st.detail && this.staffTab === 'docs') {
        this.loadDocs()
      }
    },

    // ------------------------------------------------------------
    // 「書類」タブ
    // ------------------------------------------------------------

    /**
     * 今選んでいる清掃員のコード（無ければ ''）
     */
    docStaffCode() {
      const d = this.state.staff.detail
      return d && d.staff ? String(d.staff.staffDailyCleaning || '') : ''
    },

    /**
     * 書類タブの中身を取る（雇い入れタスクの履歴書・添付した書類・顔写真の有無）
     * API: akbsRegisteredList/getStaffDocs
     */
    async loadDocs() {
      const staffCode = this.docStaffCode()
      if (!staffCode || this.docs.loading) {
        return
      }
      const docs = createDocsState()
      docs.loading = true
      this.docs = docs
      try {
        const res = await backend.searchData('akbsRegisteredList/getStaffDocs', { staffCode })
        const d = res.data ? res.data.data : null
        // 取っている間に別の清掃員を選んでいたら捨てる
        if (this.docStaffCode() !== staffCode || !d) {
          return
        }
        this.docs = { ...createDocsState(), loaded: true, resume: d.resume || null, rows: d.docs || [], hasPhoto: !!d.hasPhoto, photoUrl: d.photoUrl || '', canAddResume: !!d.canAddResume }
      } catch (error) {
        console.log(error)
        this.docs = { ...createDocsState(), error: '書類の取得に失敗しました。タブを押し直してください。' }
      }
    },

    /**
     * バイト数の表示（KB・MB）
     */
    sizeText(bytes) {
      const n = Number(bytes) || 0
      if (n >= 1024 * 1024) {
        return `${(n / 1024 / 1024).toFixed(1)} MB`
      }
      return `${Math.max(1, Math.round(n / 1024))} KB`
    },

    /**
     * 書類を添付する（ボタンでファイルを選んだとき。選んだ瞬間に保存）
     * @param {String} kind resume（履歴書。取れなかったときだけ・1 つ）か other（複数可）
     */
    pickDoc(kind, event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      this.saveDocs(kind, kind === 'resume' ? files.slice(0, 1) : files)
    },

    /**
     * 書類の枠の上にファイルを持ってきた（枠を光らせる）。保存中は受けない
     */
    onDocDragEnter(event) {
      if (this.docs.saving || !hasFiles(event)) {
        return
      }
      this.docs.dragDepth++
      this.docs.dragging = true
    },

    onDocDragLeave() {
      if (this.docs.dragDepth > 0) {
        this.docs.dragDepth--
      }
      if (this.docs.dragDepth === 0) {
        this.docs.dragging = false
      }
    },

    /**
     * 書類の枠にファイルを落とした（「書類を追加」と同じ。種類は other）
     */
    onDocDrop(event) {
      this.docs.dragDepth = 0
      this.docs.dragging = false
      if (this.docs.saving || !hasFiles(event)) {
        return
      }
      this.saveDocs('other', Array.from(event.dataTransfer.files))
    },

    /**
     * 書類を保存する（ボタンでもドロップでもここ）。種類と大きさを全部先に見て、だめなものがあれば 1 つも保存しない
     * @param {String} kind resume か other
     * @param {File[]} files
     * API: akbsRegisteredList/saveStaffDoc
     */
    async saveDocs(kind, files) {
      const staffCode = this.docStaffCode()
      if (!files.length || !staffCode) {
        return
      }
      const badExt = files.filter(f => !DOC_EXTS.includes(extOf(f.name)))
      if (badExt.length) {
        await dialogs.showErrorDialog('書類の添付', `${badExt.map(f => `「${f.name}」`).join('')}は置けない種類です。PDF・JPEG・PNG・Word・Excel を選んでください。`)
        return
      }
      const tooBig = files.filter(f => f.size > DOC_MAX_BYTES)
      if (tooBig.length) {
        await dialogs.showErrorDialog('書類の添付', `${tooBig.map(f => `「${f.name}」（${this.sizeText(f.size)}）`).join('')}は大きすぎます。10 MB までにしてください。`)
        return
      }
      if (kind === 'resume') {
        const answer = await dialogs.showConfirmDialog('履歴書の添付', `「${files[0].name}」をこの清掃員の履歴書として登録します。履歴書は後から消せません。よろしいですか。`)
        if (answer !== 'YES') {
          return
        }
      }
      this.docs.saving = true
      // 途中で失敗したら、どこまで入ったかを知らせる
      let savedCount = 0
      try {
        for (const file of files) {
          const data = await readFileAsDataUrl(file)
          const res = await backend.postData('akbsRegisteredList/saveStaffDoc', {
            staffCode,
            kind,
            // 種類は自由入力。その他は拡張子を除いたファイル名を初期値にして、変えたければ行の欄で直してもらう
            name: kind === 'resume' ? '履歴書' : stripExt(file.name),
            fileName: file.name,
            data,
            updateUser: this.$store.getters.user.id
          })
          savedCount++
          const row = res.data ? res.data.data : null
          if (row && this.docStaffCode() === staffCode) {
            // 履歴書は先頭、ほかは最後に足す（取り直したときの並びと同じ）
            if (kind === 'resume') {
              this.docs.rows.unshift(row)
              this.docs.canAddResume = false
            } else {
              this.docs.rows.push(row)
            }
          }
        }
      } catch (e) {
        console.log(e)
        const failed = files[savedCount]
        const msg = files.length > 1
          ? `「${failed.name}」を保存できませんでした（${savedCount} 件は保存済み）。残りをもう一度お試しください。`
          : '書類を保存できませんでした。もう一度お試しください。'
        await dialogs.showErrorDialog('書類の添付', msg)
      } finally {
        this.docs.saving = false
      }
    },

    /**
     * 書類の種類（自由入力）を保存する（欄を離れて値が変わったとき。履歴書は固定）
     * API: akbsRegisteredList/updateStaffDoc
     */
    async saveDocName(d) {
      if (d.kind === 'resume') {
        return
      }
      const name = String(d.name || '').trim()
      d.name = name
      try {
        await backend.postData('akbsRegisteredList/updateStaffDoc', { id: d.id, staffCode: this.docStaffCode(), name })
        // 保存した合図（2 秒だけ。続けて別の行を保存したら新しい行に移る）
        this.docs.savedId = d.id
        clearTimeout(this.docSavedTimer)
        this.docSavedTimer = setTimeout(() => {
          if (this.docs.savedId === d.id) {
            this.docs.savedId = ''
          }
        }, 2000)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('書類の種類', '種類を保存できませんでした。もう一度お試しください。')
        // 保存できなかったので、表の今の値に戻す
        this.loadDocs()
      }
    },

    /**
     * 書類を消す（確認あり。履歴書は消せない。S3 のファイルは残る）
     * API: akbsRegisteredList/deleteStaffDoc
     */
    async deleteDoc(d) {
      if (d.kind === 'resume') {
        return
      }
      const answer = await dialogs.showConfirmDialog('書類の削除', `「${d.fileName}」を消します。よろしいですか。`)
      if (answer !== 'YES') {
        return
      }
      this.docs.saving = true
      try {
        await backend.postData('akbsRegisteredList/deleteStaffDoc', { id: d.id, staffCode: this.docStaffCode() })
        this.docs.rows = this.docs.rows.filter(x => x.id !== d.id)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('書類の削除', '書類を消せませんでした。もう一度お試しください。')
      } finally {
        this.docs.saving = false
      }
    },

    /**
     * 顔写真を登録・差し替える（出力画面と同じ API。バックで向きを直して小さくし、S3 に置く）
     * API: akbsOutput/savePhoto
     */
    async pickPhoto(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      const staffCode = this.docStaffCode()
      if (!file || !staffCode) {
        return
      }
      if (!['jpg', 'jpeg', 'png'].includes(extOf(file.name))) {
        await dialogs.showErrorDialog('顔写真', `「${file.name}」は置けない種類です。JPEG か PNG の写真を選んでください。`)
        return
      }
      this.docs.photoSaving = true
      try {
        const data = await readFileAsDataUrl(file)
        await backend.postData('akbsOutput/savePhoto', { staffCode, name: file.name, data, updateUser: this.$store.getters.user.id })
        if (this.docStaffCode() === staffCode) {
          this.docs.hasPhoto = true
          // 小さく出す写真は新しい URL が要るので取り直す（photoSaving は finally で戻る）
          this.docs.photoSaving = false
          await this.loadDocs()
        }
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('顔写真', '顔写真を保存できませんでした。画像のファイルか確かめて、もう一度お試しください。')
      } finally {
        this.docs.photoSaving = false
      }
    },

    /**
     * 顔写真を消す（確認あり。表の行だけ消え、名札は写真なしになる）
     * API: akbsOutput/deletePhoto
     */
    async deletePhoto() {
      const d = this.state.staff.detail
      const name = d && d.staff ? d.staff.nameStaff : ''
      const answer = await dialogs.showConfirmDialog('顔写真の削除', `${name} の顔写真を消します。よろしいですか。\n野村・三菱の名札は写真の枠が空になります。`)
      if (answer !== 'YES') {
        return
      }
      this.docs.photoSaving = true
      try {
        await backend.postData('akbsOutput/deletePhoto', { staffCode: this.docStaffCode() })
        this.docs.hasPhoto = false
        this.docs.photoUrl = ''
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('顔写真', '顔写真を消せませんでした。もう一度お試しください。')
      } finally {
        this.docs.photoSaving = false
      }
    },

    /**
     * 雇用指示・契約詳細のカードから「清掃員の内容を見る」を押した
     * 清掃員の軸に切り替えて、その清掃員を選んだ状態にする（一覧はその清掃員コードで検索し直す）
     * @param {String} staffCode 清掃員コード
     */
    async toStaff(staffCode) {
      if (!staffCode) {
        return
      }
      this.axis = 'staff'
      const st = this.state.staff
      st.filter.name = String(staffCode)
      // 退職者でも見つかるよう全員から探す
      st.filter.scope = 'all'
      this.staffTab = 'staff'
      await this.getList(1)
      const row = st.list.find(r => r.staffDailyCleaning === String(staffCode))
      if (!row) {
        // 見つからなければ、前に選んでいた別人の詳細が右側に残らないように消す
        st.selectedKey = ''
        st.detail = null
        return
      }
      await this.select(row)
    },

    /**
     * 清掃員の編集を始める（今の値を編集用に写す）
     */
    startEdit() {
      const s = this.state.staff.detail ? this.state.staff.detail.staff : null
      if (!s) {
        return
      }
      this.edit = {
        birthday: s.birthday || '',
        sex: s.sex || '',
        numberPhoneStaff: s.numberPhoneStaff || '',
        numberMobilePhoneStaff: s.numberMobilePhoneStaff || ''
      }
      this.editing = true
    },

    /**
     * 清掃員の編集をやめる（値は戻す）
     */
    cancelEdit() {
      this.editing = false
    },

    /**
     * 緊急連絡先を保存したあとの知らせ（清掃員・家族はメインタスクを作るので noticeWithChangeTask）
     * この画面の保存は申請を通らず本番の AKBS を直接書き換えるので、総務の書類（給与奉行など）と食い違わないよう人事総務経理へ連絡してもらう
     */
    async noticeAfterSave() {
      await dialogs.showInfoDialog('保存しました', '人事総務経理に変更したことをご連絡ください。')
    },

    /**
     * 清掃員の編集を保存する（本番のAKBSの清掃員マスタを書き換える）
     * 変更した項目があれば、人事総務経理へのメインタスク（業務依頼）を作る（給与奉行などの書類と食い違わないように）
     */
    async saveEdit() {
      const st = this.state.staff
      const s = st.detail ? st.detail.staff : null
      if (!s) {
        return
      }
      if (this.mobileError) {
        await dialogs.showErrorDialog('携帯番号', this.mobileError)
        return
      }
      const answer = await dialogs.showConfirmDialog(
        '清掃員の保存',
        `${s.nameStaff} さんの生年月日・性別・電話番号・携帯番号を保存します。よろしいですか？\n変更した項目があれば、人事総務経理へのメインタスク（業務依頼）を作ります。`
      )
      if (answer !== 'YES') {
        return
      }

      const after = {
        birthday: this.edit.birthday || '',
        sex: this.edit.sex || '',
        numberPhoneStaff: String(this.edit.numberPhoneStaff || '').trim(),
        numberMobilePhoneStaff: String(this.edit.numberMobilePhoneStaff || '').trim()
      }
      // 変更した項目（見出し・前・後）。保存の前に取る（保存すると s が取り直した値に変わる）
      const changes = this.staffChanges(s, after)

      this.saving = true
      try {
        const res = await backend.postData('akbsRegisteredList/updateStaff', {
          staffCode: s.staffDailyCleaning,
          ...after,
          updateUser: this.$store.getters.user.id
        })
        const saved = res.data ? res.data.data : null
        if (!saved) {
          throw new Error('清掃員が見つかりません')
        }
        // 取り直した値で画面を差し替える（更新日時も新しくなる）
        st.detail = { ...st.detail, staff: saved }
        this.editing = false
      } catch (error) {
        console.log(error)
        await dialogs.showErrorDialog('清掃員の保存', '保存に失敗しました。もう一度お試しください。')
        this.saving = false
        return
      }

      // 保存は済んでいるので、タスクが作れなくても保存は取り消さない（人事総務経理へ連絡してもらう）
      try {
        await this.noticeWithChangeTask(
          s,
          changes.map(c => c.caption),
          changes.map(c => `${c.caption}：${c.before} → ${c.after}`)
        )
      } finally {
        this.saving = false
      }
    },

    /**
     * 保存したあとの知らせ。変わった内容があれば人事総務経理へのメインタスクを作り、その結果をダイアログに出す
     * @param {Object} s 保存前の清掃員
     * @param {Array} captions 変わった項目の見出し（タイトルに使う）
     * @param {Array} lines 変わった内容の文言（タスクの内容に使う）。空なら変化なし
     */
    async noticeWithChangeTask(s, captions, lines) {
      if (!lines.length) {
        await dialogs.showInfoDialog('保存しました', '変更した内容は無いので、メインタスクは作っていません。')
        return
      }
      const made = await this.registerStaffChangeTask(s, captions, lines)
      if (made) {
        await dialogs.showInfoDialog('保存しました', `人事総務経理へのメインタスク（No.${made.requestNo}）を作りました。\n変更した項目：${captions.join('・')}`)
      } else {
        await dialogs.showInfoDialog('保存しました', 'メインタスクを作れませんでした。人事総務経理に変更したことをご連絡ください。')
      }
    },

    /**
     * 清掃員タブで直せる項目のうち、変わったもの
     * @param {Object} s 保存前の清掃員
     * @param {Object} after 保存する値（birthday・sex・numberPhoneStaff・numberMobilePhoneStaff）
     * @returns {Array} [{ caption, before, after }]（表示用の文字。空は「なし」）
     */
    staffChanges(s, after) {
      const text = (key, v) => {
        if (v === null || v === undefined || v === '') return 'なし'
        return key === 'birthday' ? this.slash(v) : String(v)
      }
      return this.staffItems
        .filter(it => it.edit)
        .map(it => {
          const key = it.edit.key
          return { caption: it.caption, before: text(key, s[key]), after: text(key, after[key]) }
        })
        .filter(c => c.before !== c.after)
    },

    /**
     * 家族の変更（変更前の行と変更後の行をそのまま並べる。行数は揃えない）
     * @param {Array} before 保存前の家族
     * @param {Array} after 保存後の家族（バックから返った行）
     * @returns {Array} 変更前の行・「↓（下記に変更）」・変更後の行。変化なしなら空
     */
    familyChanges(before, after) {
      const cols = LIST_COLS.family
      const text = v => String(v || '').trim() || 'なし'
      const line = r => cols.map(c => `${c.caption}：${text(r[c.key])}`).join('\u3000')
      // 行が無いときは「（登録なし）」の 1 行（↓ の上下が空にならないように）
      const lines = rows => (rows.length ? rows.map(line) : ['（登録なし）'])
      const beforeLines = lines(before)
      const afterLines = lines(after)
      if (beforeLines.join('\n') === afterLines.join('\n')) {
        return []
      }
      return [...beforeLines, '\u3000\u3000↓\u3000（下記に変更）', ...afterLines]
    },

    /**
     * 清掃員の変更を人事総務経理に知らせるメインタスク（業務依頼）を作る
     * 依頼先は人事総務経理の「未定」、依頼主はログインユーザー、期限は今日
     * @param {Object} s 保存前の清掃員
     * @param {Array} captions 変わった項目の見出し（タイトルに使う）
     * @param {Array} lines 変わった内容の文言（内容に使う。変更前と変更後が分かる形）
     * @returns {Object|null} { id, requestNo }。作れなければ null
     */
    async registerStaffChangeTask(s, captions, lines) {
      try {
        const user = this.$store.getters.user
        const today = utils.getToday()

        // 宛先はマスタから引く（無ければ決め打ちの id）
        const department = (this.$store.getters.departmentList || []).find(d => d.name === CHANGE_TASK_DEPARTMENT)
        const departmentId = department ? department.departmentId : CHANGE_TASK_DEPARTMENT_ID
        const staff = (this.$store.getters.userDepartment || []).find(u => u.departmentId == departmentId && u.userName === CHANGE_TASK_STAFF)

        const rp = requestPostManager.create()
        rp.category = ['業務依頼']
        rp.category2 = ['社内']
        rp.inquiry_title = '清掃員情報の変更\u3000' + (s.nameStaff || '') + `（${captions.join('・')}）`
        rp.note = [
          'AKBS登録済み一覧から、清掃員の情報を変更しました。',
          '給与奉行の変更をお願いします。',
          '',
          `清掃員コード：${s.staffDailyCleaning || ''}`,
          `氏名：${s.nameStaff || ''}`,
          '',
          ...lines,
          '',
          '変更者：' + user.user_name + '\u3000' + moment().format('YYYY-MM-DD HH:mm')
        ].join('\r\n')
        rp.toDepartment = CHANGE_TASK_DEPARTMENT
        rp.toDepartmentId = departmentId
        rp.toStaffId = staff ? String(staff.userId) : CHANGE_TASK_STAFF_ID
        rp.toStaffName = CHANGE_TASK_STAFF
        rp.byUserId = user.id
        rp.byUser = user.user_name
        rp.requestDate = today
        rp.limitDate = today
        rp.kubunLimit = utils.getKubunLimit(today)

        const saved = await requestPostManager.register(this, rp)
        return saved && saved.id ? saved : null
      } catch (error) {
        console.log('registerStaffChangeTask error !!!')
        console.log(error)
        return null
      }
    },

    /**
     * 家族・緊急連絡先の編集を始める（今の行を編集用に複製する）
     * @param {String} kind 'family' か 'emergency'
     */
    startListEdit(kind) {
      const d = this.state.staff.detail
      if (!d) {
        return
      }
      const cols = LIST_COLS[kind] || []
      const rows = (d[kind] || []).map(src => {
        const r = {}
        cols.forEach(c => { r[c.key] = src[c.key] || '' })
        return r
      })
      this.listEdit = { kind, rows }
    },

    /**
     * 家族・緊急連絡先に空の行を足す
     */
    addListRow() {
      const r = {}
      this.listCols.forEach(c => { r[c.key] = '' })
      this.listEdit.rows.push(r)
    },

    /**
     * 家族・緊急連絡先の行を消す（保存するまで本番は変わらない）
     * @param {Number} i 行の位置
     */
    removeListRow(i) {
      this.listEdit.rows.splice(i, 1)
    },

    /**
     * 家族・緊急連絡先の編集をやめる
     */
    cancelListEdit() {
      this.listEdit = { kind: '', rows: [] }
    },

    /**
     * 列の形式エラー（〒・携帯番号）
     * @param {Object} col 列
     * @param {Object} r 行
     */
    colError(col, r) {
      return col.format ? formatError(col.format, r[col.key]) : ''
    },

    /**
     * 家族・緊急連絡先の編集を保存する（本番の同じ清掃員の分を全件入れ直す）
     * 全部空の行は保存しない。氏名が無い行・形式エラーがある行があれば止める
     * 家族は変わった内容（追加・変更・削除）があれば、人事総務経理へのメインタスク（業務依頼）を作る
     */
    async saveListEdit() {
      const st = this.state.staff
      const s = st.detail ? st.detail.staff : null
      const kind = this.listEdit.kind
      if (!s || !kind) {
        return
      }
      const name = kind === 'family' ? '家族' : '緊急連絡先'
      const cols = this.listCols

      // 全部空の行は画面からも取り除いてから確かめる（エラー文の「N行目」が画面の行と合うように）
      this.listEdit.rows = this.listEdit.rows.filter(r => cols.some(c => String(r[c.key] || '').trim()))
      const rows = this.listEdit.rows
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]
        if (!String(r.name || '').trim()) {
          await dialogs.showErrorDialog(`${name}の保存`, `${i + 1}行目：氏名を入力してください`)
          return
        }
        const err = cols.map(c => this.colError(c, r)).find(e => e)
        if (err) {
          await dialogs.showErrorDialog(`${name}の保存`, `${i + 1}行目：${err}`)
          return
        }
      }
      // 家族は変わった内容があれば人事総務経理へのメインタスクを作る（緊急連絡先は連絡してもらうだけ）
      const withTask = kind === 'family'
      const answer = await dialogs.showConfirmDialog(
        `${name}の保存`,
        `${s.nameStaff} さんの${name}（${rows.length}件）を保存します。よろしいですか？` +
          (withTask ? '\n変更した内容があれば、人事総務経理へのメインタスク（業務依頼）を作ります。' : '')
      )
      if (answer !== 'YES') {
        return
      }

      const trimmed = rows.map(r => {
        const t = {}
        cols.forEach(c => { t[c.key] = String(r[c.key] || '').trim() })
        return t
      })
      // 保存前の行（変わった内容を出すのに使う。保存すると画面の行は取り直した値に変わる）
      const before = (st.detail[kind] || []).slice()
      // 変わった内容。比べる相手は保存後にバックから返る行（住所の全角→半角などバックが直した後の値で比べる）
      let lines = []

      this.saving = true
      try {
        const api = withTask ? 'updateFamily' : 'updateEmergency'
        const res = await backend.postData(`akbsRegisteredList/${api}`, {
          staffCode: s.staffDailyCleaning,
          rows: trimmed,
          updateUser: this.$store.getters.user.id
        })
        const saved = res.data && Array.isArray(res.data.data) ? res.data.data : []
        st.detail = { ...st.detail, [kind]: saved }
        this.listEdit = { kind: '', rows: [] }
        if (withTask) {
          lines = this.familyChanges(before, saved)
        }
      } catch (error) {
        console.log(error)
        await dialogs.showErrorDialog(`${name}の保存`, '保存に失敗しました。もう一度お試しください。')
        this.saving = false
        return
      }

      // 保存は済んでいるので、タスクが作れなくても保存は取り消さない
      try {
        if (withTask) {
          await this.noticeWithChangeTask(s, [name], lines)
        } else {
          await this.noticeAfterSave()
        }
      } finally {
        this.saving = false
      }
    },

    /**
     * 物件名（バックは2段の物件名を改行でつないでいるので、空白にして1行で出す）
     * @param {Object} row 雇用指示
     */
    siteNameText(row) {
      return String(row.siteName || row.cdSite || '').replace(/\n/g, ' ')
    },

    /**
     * 一覧表の行の開始日（雇用指示は勤務の開始日、契約詳細は契約の開始日）
     * @param {Object} row 行
     * @param {Boolean} emp 雇用指示の行か
     */
    rowStart(row, emp) {
      return emp ? row.startDateWork : row.startDateContract
    },

    /**
     * 一覧表の行の終了日
     * @param {Object} row 行
     * @param {Boolean} emp 雇用指示の行か
     */
    rowEnd(row, emp) {
      return emp ? row.endDateWork : row.endDateContract
    },

    /**
     * 一覧表の行の祝日区分（雇用指示と契約詳細でキーが違う）
     * @param {Object} row 行
     * @param {Boolean} emp 雇用指示の行か
     */
    rowHoliday(row, emp) {
      return emp ? row.cdKubunHoliday : row.kubunHolidayContract
    },

    /**
     * 一覧表の行の有・無の項目（ゴミ出し・全社休日出勤。契約詳細の軸では時間指定の列もあるので、雇用指示の行は空で埋める）
     * @param {Object} row 行
     * @param {Boolean} emp 雇用指示の行か
     */
    rowFlags(row, emp) {
      if (emp) {
        const flags = [
          { key: 'garbage', value: row.hasCarryingoutGarbage },
          { key: 'companysHoliday', value: row.hasExportingCompanysHoliday }
        ]
        if (!this.isEmploymentAxis) {
          flags.push({ key: 'specificTime', value: null })
        }
        return flags
      }
      return [
        { key: 'garbage', value: row.hasCarryingoutGarbageContract },
        { key: 'companysHoliday', value: row.hasExportingCompanysHolidayContract },
        { key: 'specificTime', value: row.specificTime }
      ]
    },

    // 雇用指示の一覧表の表示用の文字（カードと同じ utilsAKBS を使う）
    isDayOff(row, w) {
      return utilsAKBS.isDayOff(row, w)
    },

    attendanceNames(codes) {
      return utilsAKBS.attendanceNames(codes)
    },

    hoursText(hours) {
      return utilsAKBS.hoursText(hours)
    },

    holidayName(code) {
      return utilsAKBS.holidayName(code)
    },

    yesNo(val) {
      return utilsAKBS.yesNo(val)
    },

    /**
     * 値が入っているか（null・undefined・空文字は未設定）
     * @param {*} val 値
     */
    hasValue(val) {
      return val !== null && val !== undefined && val !== ''
    },

    /**
     * 有・無のバッジの色（有（0以外）は藍色、無（0）は灰色）
     * @param {*} val 値（有無なら 1/0、祝日区分なら 0 が無）
     */
    flagColor(val) {
      return Number(val) === 0 ? 'gray' : 'indigo'
    },

    /**
     * 契約詳細の行のキー（物件コード＋契約コード＋契約開始日。契約詳細情報は同じ契約コードに開始日ごとの行があるため）
     * @param {Object} row 契約詳細
     */
    contractKey(row) {
      return `${row.cdSite}-${row.cdContract}-${row.startDateContract || ''}`
    },

    /**
     * 終了した雇用指示の行を隠す（showEnded なら全部）
     * @param {Array} rows 雇用指示の行
     */
    filterEnded(rows) {
      const list = Array.isArray(rows) ? rows : []
      return this.showEnded ? list : list.filter(e => !e.isEnded)
    },

    /**
     * 退職しているか（退職日が今日より前）
     * @param {Object} row 清掃員
     */
    isRetired(row) {
      const d = row && row.dateResignation
      return !!d && String(d).replace(/-/g, '/') < this.today()
    },

    /**
     * 年齢（今日時点。誕生日が来ていなければ1つ引く）
     * @param {String} birthday 生年月日（yyyy-mm-dd）
     */
    age(birthday) {
      const parts = String(birthday || '').split('-')
      if (parts.length !== 3) {
        return ''
      }
      const y = Number(parts[0])
      const m = Number(parts[1])
      const d = Number(parts[2])
      const now = new Date()
      let age = now.getFullYear() - y
      if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) {
        age--
      }
      return isNaN(age) ? '' : age
    },

    /**
     * 今日（yyyy/mm/dd）
     */
    today() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`
    },

    /**
     * 金額の表示（1,200円。無ければ -）
     * @param {*} val 金額
     */
    yen(val) {
      if (val === null || val === undefined || val === '') {
        return '-'
      }
      return `${Number(val).toLocaleString()}円`
    },

    /**
     * yyyy-mm-dd → yyyy/mm/dd（無ければ空）
     * @param {String} day 日付
     */
    slash(day) {
      return day ? String(day).replace(/-/g, '/') : ''
    },

    /**
     * セッションストレージに控えた軸・絞り込みを戻す（無ければ何もしない）
     */
    getSessionStorage() {
      let item = null
      try {
        item = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_NAME))
      } catch (e) {
        return
      }
      if (!item) {
        return
      }
      if (item.axis && AXES.some(a => a.key === item.axis)) {
        this.axis = item.axis
      }
      for (const a of AXES) {
        if (item.filters && item.filters[a.key]) {
          this.state[a.key].filter = { ...this.state[a.key].filter, ...item.filters[a.key] }
        }
      }
    },

    /**
     * 軸と絞り込みをセッションストレージに控える（検索・軸の切り替えのたびに保存する）
     */
    setSessionStorage() {
      const filters = {}
      for (const a of AXES) {
        filters[a.key] = this.state[a.key].filter
      }
      sessionStorage.setItem(SESSION_STORAGE_NAME, JSON.stringify({ axis: this.axis, filters }))
    }
  }
}
</script>

<style scoped>
/* 保存中のマスク（賃金画面・契約詳細入力画面と同じ） */
.loading-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  color: #6b7280;
  font-weight: 700;
  font-size: 14px;
}

/* 左の一覧の枠 */
.list-box {
  width: 460px;
}

/* 雇用指示の一覧表（1つの表に全部の内容。画面より小さくならず、横に長いときは枠の中で横スクロール） */
.emp-table {
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  color: #1f2937;
}

/* 備考以外は折り返さない。行の間の線は1行が3段あるので少し太く濃くする。上下は中央（曜日と備考だけ上） */
.emp-table th,
.emp-table td {
  padding: 5px 8px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  border-right: 1px solid #e5e7eb;
  border-bottom: 2px solid #d1d5db;
}

.emp-table td.day,
.emp-table td.note-cell {
  vertical-align: top;
}

/* 物件が変わる行は上に藍色の太い線 */
.emp-table tr.site-top td {
  border-top: 3px solid #a5b4fc;
}

/* 物件の列は横スクロールしても左に残す（背景を付けないと下の列が透ける） */
.emp-table th.site,
.emp-table td.site {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  border-right: 2px solid #c7d2fe;
}

.emp-table thead th.site {
  z-index: 2;
  background: #e0e7ff;
}

.emp-table tbody tr:hover td.site {
  background: #f8faff;
}

.emp-table th:last-child,
.emp-table td:last-child {
  border-right: none;
}

/* 文字の列（物件・契約・清掃員・期間・備考）は左そろえ */
.emp-table .text-left {
  text-align: left;
}

/* 見出しは藍色で、縦スクロールしても上に残す。土曜は青・日曜は赤 */
.emp-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  font-size: 0.75rem;
  white-space: nowrap;
  border-bottom-color: #c7d2fe;
}

.emp-table thead th.sat {
  color: #1d4ed8;
}

.emp-table thead th.sun {
  color: #dc2626;
}

.emp-table tbody tr:hover td {
  background: #f8faff;
}

/* 終了した行は文字を薄く */
.emp-table tr.ended td {
  color: #9ca3af;
}

.emp-table .site-name {
  font-weight: bold;
  color: #1d4ed8;
  white-space: nowrap;
}

.emp-table .sub {
  font-size: 0.7rem;
  color: #6b7280;
  white-space: nowrap;
}

.emp-table .link-btn {
  font-size: 0.7rem;
  color: #2563eb;
  text-decoration: underline;
}

/* 契約の下にぶら下がった雇用指示の行（ごく薄い黄色。左に固定した物件の欄も同じ色にする） */
.emp-table tr.nested td,
.emp-table tr.nested td.site {
  background: #fffbeb;
}

.emp-table tr.nested td.day.work {
  background: #fffdf3;
}

.emp-table tr.nested td.day.rest {
  background: #fdfcf6;
}

.emp-table tbody tr.nested:hover td,
.emp-table tbody tr.nested:hover td.site {
  background: #fff7d6;
}

/* 曜日の列（勤務の有無に関わらず全部同じ幅。勤務がある日は白に近い藍色、休みはごく薄い灰色に「-」） */
.emp-table th.day,
.emp-table td.day {
  width: 104px;
  min-width: 104px;
  max-width: 104px;
  box-sizing: border-box;
  white-space: nowrap;
}

.emp-table td.day.work {
  background: #fbfcff;
}

.emp-table td.day.rest {
  background: #fcfcfc;
  color: #e5e7eb;
}

.emp-table tbody tr:hover td.day.rest {
  color: #e5e7eb;
}

.emp-table .time {
  font-weight: bold;
}

.emp-table .tilde {
  margin: 0 2px;
  color: #9ca3af;
  font-weight: normal;
}

.emp-table .hours {
  font-size: 0.75rem;
  color: #4b5563;
}

.emp-table .att-pill {
  display: inline-block;
  margin: 1px 1px;
  padding: 0 6px;
  border-radius: 9999px;
  border: 1px solid #a5b4fc;
  background: #fff;
  color: #3730a3;
  font-size: 0.65rem;
  line-height: 1.5;
}

/* 一番右の列（雇用指示は備考、契約詳細は更新日時）は残りの幅を全部使う */
.emp-table td.wide {
  width: 100%;
  min-width: 200px;
}

/* 備考の列（備考だけ折り返し、シーケンス・更新日時はセルの下に固定） */
.emp-table td.note-cell {
  position: relative;
  /* 下に固定する2行分の余白 */
  padding-bottom: 34px;
}

.emp-table .note {
  white-space: pre-wrap;
  font-size: 0.7rem;
}

.emp-table .note-meta {
  position: absolute;
  left: 8px;
  bottom: 5px;
}

/* 契約の行の編集（作業内容・時間指定・ランク・クリーンプラス金額・備考）。変えた欄だけ黄色 */
.con-edit-select.changed,
.con-edit-input.changed,
.con-edit-note.changed {
  background: #fef08a;
}

.con-edit-select,
.con-edit-input {
  height: 28px;
  padding: 0 6px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  background: #fff;
  font-size: 0.8rem;
  font-weight: normal;
}

.con-edit-select {
  width: 150px;
}

.con-edit-select.narrow {
  width: 64px;
}

.con-edit-input {
  width: 90px;
  text-align: right;
}

.con-edit-note {
  width: 100%;
  min-width: 240px;
  padding: 4px 6px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: normal;
  white-space: pre-wrap;
}

.con-btn {
  padding: 3px 14px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  background: #fff;
  color: #374151;
  font-size: 0.75rem;
  font-weight: bold;
}

/* 保存は水色（ユーザー指示） */
.con-btn.save {
  border-color: #0891b2;
  background: #0891b2;
  color: #fff;
}

/* 物件のセルの「編集」（藍） */
.con-btn.edit {
  border-color: #4338ca;
  background: #4338ca;
  color: #fff;
}

.con-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 選んでいる行 */
.selected-row {
  background-color: #dbeafe;
  font-weight: bold;
}

/* 絞り込みの入力欄は高さを揃える */
.filter-input {
  height: 38px;
  box-sizing: border-box;
}

/* 何かで絞っている欄（申請一覧と同じ色） */
.filter-input.filled {
  font-weight: bold;
  background-color: #efdf8a;
}

/* 軸のタブ（ヘッダーの一番上の行の真ん中。白い枠に、選択中は青・それ以外は灰色のボタン） */
.axis-tabs {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 6px;
  padding: 5px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.axis-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  background: #9ca3af;
  color: #fff;
  font-size: 0.875rem;
  font-weight: bold;
  white-space: nowrap;
  cursor: pointer;
}

.axis-tab:hover {
  background: #6b7280;
}

.axis-tab.active {
  background: #1d4ed8;
}

.axis-tab.active:hover {
  background: #1e40af;
}

/* 右の見出し */
.detail-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #eef2ff;
  border-radius: 6px 6px 0 0;
}

/* 清掃員の中のタブ（全部同じ幅。一番長い「緊急連絡先」+ 1桁の件数が収まる幅） */
/* 幅は全部同じ（一番長い「緊急連絡先」＋アイコン＋件数が 1 行に収まる幅。折り返さない） */
.tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 150px;
  padding: 6px 8px;
  white-space: nowrap;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
}

.tab.active {
  background: #fff;
  color: #3730a3;
  border-color: #c7d2fe;
  position: relative;
  top: 1px;
}

.tab-count {
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 9999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.7rem;
}

/* 清掃員マスタ（見出し・値の2列） */
.detail-grid {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 0;
  max-width: 900px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  font-size: 0.875rem;
}

.detail-caption {
  padding: 6px 12px;
  background: #f9fafb;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
}

.detail-value {
  padding: 6px 12px;
  border-bottom: 1px solid #e5e7eb;
  font-weight: bold;
  color: #1f2937;
}

/* 直せる項目の印（見出しの右に小さく藍の札） */
.detail-caption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.editable-mark {
  flex: none;
  padding: 1px 6px;
  border-radius: 9999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.7rem;
  font-weight: bold;
  line-height: 1.4;
}

/* 編集中の項目（直せる4項目だけ黄色にして分かるようにする） */
.detail-caption.editing,
.detail-value.editing {
  background: #fefce8;
}

.edit-input {
  width: 260px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: normal;
}

/* 家族・緊急連絡先の編集の入力欄（住所は広く、それ以外は狭く） */
.edit-input.narrow {
  width: 150px;
}

.edit-input.wide {
  width: 320px;
}

/* 家族・緊急連絡先の表 */
.data-table {
  border-collapse: collapse;
  font-size: 0.875rem;
  white-space: nowrap;
}

.data-table th,
.data-table td {
  border: 1px solid #e5e7eb;
  padding: 5px 10px;
  text-align: left;
}

.data-table th {
  background: #f3f4f6;
  color: #4b5563;
  font-weight: normal;
  font-size: 0.75rem;
}

.empty-text {
  font-size: 0.875rem;
  color: #9ca3af;
}

/* 「書類」タブ：顔写真と書類の枠 */
.doc-block {
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
}

.doc-block-title {
  margin-bottom: 6px;
  font-weight: bold;
  color: #3730a3;
}

/* 書類を足すドロップ枠（表の下。藍の太い点線で目立たせる。押してもファイルを選べる） */
.doc-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 10px;
  padding: 40px 16px;
  border: 3px dashed #4338ca;
  border-radius: 8px;
  background: #eef2ff;
  color: #3730a3;
  cursor: pointer;
  user-select: none;
}

.doc-drop:hover {
  background: #e0e7ff;
}

/* ファイルを枠の上まで持ってきたとき（実線・濃い色） */
.doc-drop.is-dragging {
  border-style: solid;
  background: #c7d2fe;
}

.doc-drop.is-off {
  border-color: #9ca3af;
  background: #f3f4f6;
  color: #6b7280;
  cursor: default;
}

/* 中の要素に dragleave を飛ばさない */
.doc-drop > * {
  pointer-events: none;
}

.doc-drop-icon {
  flex: none;
}

.doc-drop-main {
  font-size: 1.05rem;
  font-weight: bold;
}

.doc-drop-sub {
  margin-top: 2px;
  font-size: 0.8rem;
}

/* 顔写真の縮小表示（名札と同じ縦長の枠。押すと元の大きさ） */
.photo-thumb {
  width: 48px;
  height: 64px;
  object-fit: cover;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #f3f4f6;
}

/* ファイルを選ぶボタン（label の中の input[type=file] は隠す） */
.file-btn {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #4338ca;
  border-radius: 4px;
  background: #4338ca;
  color: #fff;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.file-btn:hover {
  background: #3730a3;
}

/* 「履歴書を添付」は水色（ユーザー指示） */
.file-btn.resume {
  border-color: #0891b2;
  background: #0891b2;
}

.file-btn.resume:hover {
  background: #0e7490;
}

.file-btn.danger {
  border-color: #dc2626;
  background: #fff;
  color: #dc2626;
}

.file-btn.danger:hover {
  background: #fef2f2;
}

.file-btn.is-off,
.file-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 「種類」の自由入力（欄を離れたら保存）。空のときは薄い赤で入力を促す */
.doc-table .doc-name-input {
  width: 11rem;
  height: 1.75rem;
  padding: 0 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  font-weight: 700;
}
.doc-table .doc-name-input.empty {
  background: #fef2f2;
}
.doc-table .doc-name-input:disabled {
  background: #f3f4f6;
}
/* 右端の削除の列は左右中央（横に広げたセル「書類はありません」などは左のまま） */
.doc-table th:last-child,
.doc-table td:last-child:not([colspan]) {
  text-align: center;
}
/* 保存した直後：枠を緑に */
.doc-table .doc-name-input.saved {
  border-color: #16a34a;
  box-shadow: 0 0 0 1px #16a34a;
}
.doc-table .doc-saved {
  display: inline-flex;
  align-items: center;
  color: #16a34a;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.doc-table .doc-link {
  font-weight: bold;
  color: #1d4ed8;
}

.doc-table .doc-link:hover {
  text-decoration: underline;
}

/* ページネーション（申請一覧と同じ見た目） */
#pagenation >>> ul {
  display: flex !important;
  color: rgb(6, 125, 194);
  font-weight: bold !important;
  cursor: pointer;
}

#pagenation >>> ul > li {
  padding: 0 10px;
}

#pagenation >>> ul > .active {
  background: rgb(6, 125, 194);
  color: white;
}

#pagenation >>> p {
  color: gray;
  font-size: small;
}
</style>
